import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import apis from '@src/apis';
import {
  getSavedPrice,
  verifyPolicy,
  verifyTime,
  validateVoucherByRegion,
  getOptimalVouchersForPackage,
  isFixedCharacterVoucherUsable,
} from '@src/services/voucher';
import { COLOR } from '@src/styles/color';
import GiftIcon from '@src/assets/icons/gift-filled-icon.svg';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Tooltip from '@src/components/Tooltip';
import {
  delimitNumber,
  formatNumber,
  getRandomIntNumber,
} from '@src/utils/number';
import {
  TIME_TYPE,
  VOUCHER_REGION,
  VOUCHER_TYPE,
} from '@src/constants/voucher';
import actions from '@src/redux/actions';
import { checkVietNam } from '@src/utils/checkCountry';
import pluralize from '@src/utils/pluralize';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { PACK_DURATION } from '@src/constants/package';
import {
  getPackageCurrentPrice,
  checkCustomPackage,
} from '@src/services/package';
import camelcaseKeys from 'camelcase-keys';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import { StyledVoucherBox } from './index.style';

const getPromotion = ({
  voucher,
  packageState,
  language,
  t,
  isVietNam,
  credits,
}) => {
  switch (voucher?.type) {
    case VOUCHER_TYPE.PRICE:
      return (
        <span>
          {t('reduce')}{' '}
          <span
            className={`discount-unit-price ${
              isVietNam && 'underline'
            } font-size-16`}
          >
            {isVietNam ? 'đ' : '$'}
          </span>
          {voucher.value.toLocaleString(language)}
        </span>
      );
    case VOUCHER_TYPE.PERCENT:
      return `${t('reduce')} ${voucher.value}%`;
    case VOUCHER_TYPE.CHARACTER: {
      const { creditsPerCycle } = packageState?.cycle || {};
      const textMultiply = `${t('multiply')} x${voucher.value} ${pluralize(
        voucher.value,
        t('credit'),
        language,
      )}`;

      return `${textMultiply} ${
        creditsPerCycle
          ? `(+${t('creditsPerMonth', {
              creditsPerMonth: delimitNumber(
                creditsPerCycle * (voucher.value - 1),
                language,
              ),
            })})`
          : `(+${delimitNumber(
              (credits || packageState.maxCharacters) * (voucher.value - 1),
              language,
            )} ${pluralize(
              (credits || packageState.maxCharacters) * (voucher.value - 1),
              t('credit'),
              language,
            )})`
      }`;
    }
    case VOUCHER_TYPE.FIXED_CHARACTER: {
      const { type, level, cycle = {}, expiresIn } = packageState || {};
      const { totalCycles } = cycle;
      const isMonthlyPackage = expiresIn === PACK_DURATION.MONTH;
      const isCustomPackage = checkCustomPackage({ type, level });
      let label = '';

      if (isMonthlyPackage || isCustomPackage) {
        label = `${t('addition')} ${delimitNumber(
          voucher.value,
          language,
        )} ${pluralize(voucher.value, t('credit'), language)}`;
        return label;
      }

      const fixedCharacterPerCycle = Math.ceil(voucher.value / totalCycles);
      label = `${t('addition')} ${delimitNumber(
        voucher.value,
        language,
      )} ${pluralize(voucher.value, t('credit'), language)} (+${t(
        'creditsPerMonth',
        {
          creditsPerMonth: delimitNumber(fixedCharacterPerCycle, language),
        },
      )})`;
      return label;
    }
    case VOUCHER_TYPE.TIME:
      if (voucher?.timeType === TIME_TYPE.MONTH) {
        return `${t('addFreeMonths', { value: voucher.value })}`;
      }
      return `${t('extra')} ${voucher.value} ${t('dayLowercase')}`;
    case VOUCHER_TYPE.PERCENT_TIME:
      return `${t('donate')} ${voucher.value}% ${t('timeLowercase')}`;

    default:
      return voucher.description;
  }
};

const SelectedVoucherBox = ({
  voucher,
  setSelectedVoucher,
  customPackagePrice,
  credits,
}) => {
  const { i18n, t } = useTranslation();
  const { language } = i18n;

  const { package: packageState } = useSelector((state) => state.order);

  const isVietNam = checkVietNam();

  const moneyVoucherType = [VOUCHER_TYPE.PERCENT, VOUCHER_TYPE.PRICE];

  const { price: basePrice, usdPrice: baseUsdPrice } = getPackageCurrentPrice(
    packageState,
    true,
  );

  const isCustomPackage = checkCustomPackage({
    type: packageState.type,
    level: packageState.level,
  });

  const calculateVoucherDiscount = () => {
    const currPrice = isCustomPackage ? customPackagePrice : basePrice;
    const currUsdPrice = isCustomPackage ? customPackagePrice : baseUsdPrice;

    const finalPrice = getSavedPrice({
      voucher,
      currPrice,
      currUsdPrice,
    });

    return formatNumber(finalPrice, language);
  };

  return (
    <>
      <Box className="selected-voucher">
        <Box display="flex" gap="4px" alignItems="center">
          <Box className="voucher-gift-icon">
            <img src={GiftIcon} alt="gift-icon" className="gift-icon" />
          </Box>
          <Typography className="voucher-name">
            {getPromotion({
              voucher,
              packageState,
              language,
              t,
              isVietNam,
              credits,
            })}
          </Typography>
        </Box>
        {moneyVoucherType.includes(voucher.type) && (
          <Typography className="voucher-value">
            -{' '}
            {!isVietNam && (
              <span className="discount-unit-price font-size-16">$</span>
            )}
            {isVietNam && (
              <span className="discount-unit-price underline font-size-16">
                đ
              </span>
            )}
            {calculateVoucherDiscount()}
          </Typography>
        )}
        <Button
          color="primary"
          variant="outlined"
          className="selected-voucher-button"
          onClick={setSelectedVoucher}
        >
          {t('unselect')}
        </Button>
      </Box>
    </>
  );
};

const VoucherBox = ({
  voucher,
  autoVouchers,
  setSelectedVoucher,
  selectedVoucher,
  checkCanApplyVoucher,
  credits,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { package: packageState } = useSelector((state) => state.order);
  const isVietNam = checkVietNam();
  const autoVoucher = autoVouchers.find(
    (item) => item.voucherCode === voucher.code,
  );
  const isAutoVoucher =
    autoVoucher?.voucherCode && checkCanApplyVoucher(autoVoucher.voucherCode);
  const showVoucher = !voucher.isPrivate || isAutoVoucher;

  const VoucherInfoTooltip = () => (
    <Box className="voucher-info-tooltip" textAlign="left">
      <Typography variant="body2">{`${t('code')}: ${
        voucher?.code
      }`}</Typography>
      <Typography variant="body2">{`${t('exp')}: ${moment(
        voucher?.fakeEndTimeForVoucher || voucher.endTime,
      ).format('DD/MM/YYYY - HH:mm')}`}</Typography>
      {voucher.policy?.applyAllPackages && (
        <Typography variant="body2">{`${t('appliedCondition')}: ${t(
          'applyAllPackages',
        )}`}</Typography>
      )}
    </Box>
  );

  return showVoucher ? (
    <Box
      className="voucher"
      sx={{
        border: selectedVoucher
          ? `2px solid ${COLOR.yellow.default} !important`
          : 'none',
        backgroundColor: selectedVoucher ? COLOR.yellow[8] : COLOR.white,
        ':hover': {
          backgroundColor: selectedVoucher ? COLOR.yellow[8] : COLOR.indigo[4],
        },
      }}
      onClick={setSelectedVoucher}
    >
      <Box
        alignItems="center"
        display="flex"
        gap="4px"
        onClick={(e) => e.stopPropagation()}
      >
        <Typography className="voucher-name">
          {getPromotion({
            voucher,
            packageState,
            language,
            t,
            isVietNam,
            credits,
          })}
        </Typography>
        <Tooltip arrow placement="top" title={<VoucherInfoTooltip />}>
          <InfoRoundedIcon
            fontSize="16px"
            className="tooltip-voucher"
            color="#1E164EA3"
          />
        </Tooltip>
      </Box>

      <Button
        color="primary"
        variant={selectedVoucher ? 'outlined' : 'contained'}
        data-id={
          selectedVoucher ? 'remove-promo-code' : 'button-apply-suggested-code'
        }
        className={
          selectedVoucher
            ? 'voucher-button'
            : 'voucher-button selected-voucher-button'
        }
        onClick={setSelectedVoucher}
        data-promo-code={voucher?.code}
      >
        {selectedVoucher ? t('unselect') : t('apply')}
      </Button>
    </Box>
  ) : (
    <div />
  );
};

const Voucher = ({ calculateCharacterPurchase, currPackage, custom = {} }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const isDubbingRoute = useCheckDubbingRoute();
  const location = useLocation();
  const history = useHistory();
  const {
    autoRenew,
    voucherCode: voucherCodeParamURL,
    bannerType,
    package: packageCodeParamURL,
    credits,
    devices,
    voiceClonings,
    concurrentRequestApi: concurrentRequestApiQuery,
    creditsPerCycle: creditsPerCycleQuery,
    redeemCode,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const autoRenewParam = useMemo(() => {
    if (autoRenew) {
      try {
        return JSON.parse(autoRenew);
      } catch (e) {
        return null;
      }
    }
    return false;
  }, [autoRenew]);

  const { autoVouchers } = useSelector((state) => state.voucher);
  const { voucher: voucherState, package: packageState } = useSelector(
    (state) => state.order,
  );
  const isUIBuyTopupCharacters = packageState?.isTopUp;

  const [openVoucherCollapse, setOpenVoucherCollapse] = useState(false);
  const [vouchersDisplay, setVouchersDisplay] = useState([]);
  const [typingVoucher, setTypingVoucher] = useState('');
  const [error, setError] = useState(null);
  const [totalVoucher, setTotalVoucher] = useState([]);

  const { language } = i18n;
  const isVietNam = checkVietNam();

  const generateDisplayVoucherName = (voucherData) => {
    const value = delimitNumber(voucherData.value, language);

    switch (voucherData.type) {
      case VOUCHER_TYPE.PERCENT:
        return `${t('reduce')} ${value}%`;
      case VOUCHER_TYPE.PRICE:
        return `${t('reduce')} ${value}VND`;
      case VOUCHER_TYPE.CHARACTER:
        return `x${value} ${pluralize(value, t('credit'), language)}`;
      case VOUCHER_TYPE.TIME:
        return `${t('extra')} ${value} ${t('day')}`;
      case VOUCHER_TYPE.PERCENT_TIME:
        return `${t('extra')} ${value}% ${t('day')}`;
      default:
        return '';
    }
  };

  const filterUseableVoucher = (voucherData, currPackageId) =>
    voucherData
      .filter((voucher) => {
        const satisfiedTime = verifyTime(voucher);
        const satisfiedPolicy = verifyPolicy(voucher, currPackageId);
        const isValidVoucherType =
          !isDubbingRoute || voucher.type !== VOUCHER_TYPE.CHARACTER;

        const isVoucherAutoRenew =
          autoRenewParam &&
          voucher.policy?.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.PREPAID;
        const isVoucherPrepaid =
          !autoRenewParam &&
          voucher.policy?.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

        const canUse =
          satisfiedTime &&
          satisfiedPolicy &&
          voucher.remainingAmount &&
          !voucher.isPrivate &&
          voucher.usedAmount < voucher.numReuse &&
          isValidVoucherType &&
          (isVoucherAutoRenew || isVoucherPrepaid);

        return canUse;
      })
      .map((voucher) => ({
        ...voucher,
        satisfiedTime: verifyTime(voucher),
        satisfiedPolicy: verifyPolicy(voucher, currPackageId),
        canUse: true,
      }))
      .sort((a) => (a.canUse ? -1 : 1));

  // Change route when apply voucher
  const changeRoute = (voucherCode) => {
    const search = queryString.stringify({
      package: packageCodeParamURL,
      autoRenew,
      ...(isUIBuyTopupCharacters || redeemCode ? {} : { voucherCode }),
      bannerType,
      ...(credits && { credits }),
      ...(devices && { devices }),
      ...(voiceClonings && { voiceClonings }),
      ...(concurrentRequestApiQuery !== undefined && {
        concurrentRequestApi: concurrentRequestApiQuery || 0,
      }),
      creditsPerCycle: creditsPerCycleQuery,
      ...(redeemCode && { redeemCode }),
    });
    history.replace({ search });
  };

  const checkSelectedVoucher = (voucherCode, vouchersToSelect, allVouchers) => {
    // Check if the selected voucher is in the list of vouchers to select
    const selectedVoucher = vouchersToSelect.find(
      (voucher) => voucher.code === voucherCode,
    );
    if (!selectedVoucher) return false;

    // Check if the selected voucher can be applied
    const matchedVoucher = allVouchers.find(
      (voucher) => voucher.code === voucherCode,
    );
    if (!matchedVoucher) return false;

    return matchedVoucher;
  };

  const applyVoucher = (matchedVoucher) => {
    dispatch(
      actions.voucher.applyVoucher({
        ...matchedVoucher,
        displayText: generateDisplayVoucherName(matchedVoucher),
      }),
    );
    dispatch(actions.order.updateVoucher(matchedVoucher));
    changeRoute(matchedVoucher.code);
  };

  const isCustomPackage = checkCustomPackage({
    type: packageState.type,
    level: packageState.level,
  });

  let orderPrice = packageState?.price;
  if (isCustomPackage) orderPrice = custom?.vndPrice;

  const handleChangeTypingVoucher = (e) => {
    setTypingVoucher(e.target.value.toUpperCase());
    setError(null);
  };

  const validateVoucher = (voucherCode) => {
    const matchedVoucher = totalVoucher.find(
      (voucher) => voucher.code === voucherCode,
    );

    if (!matchedVoucher) return false;

    const isSatisfiedTime = verifyTime(matchedVoucher);
    const isSatisfiedPolicy = verifyPolicy(matchedVoucher, currPackage.id);
    const hasRemainingAmount = matchedVoucher.remainingAmount > 0;
    const isUnderReuseLimit =
      matchedVoucher.usedAmount < matchedVoucher.numReuse;

    const isVoucherAutoRenew =
      autoRenewParam &&
      matchedVoucher.policy?.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.PREPAID;
    const isVoucherPrepaid =
      !autoRenewParam &&
      matchedVoucher.policy?.renewalType !==
        PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

    // minimumOrderPrice is set with VND currency and should only be compare with VND price of package
    const isFixedCharacterPriceMet =
      matchedVoucher.type === VOUCHER_TYPE.FIXED_CHARACTER
        ? isFixedCharacterVoucherUsable({
            voucher: matchedVoucher,
            price: orderPrice,
          })
        : true;

    return isSatisfiedTime &&
      isSatisfiedPolicy &&
      hasRemainingAmount &&
      isUnderReuseLimit &&
      (isVoucherAutoRenew || isVoucherPrepaid) &&
      isFixedCharacterPriceMet
      ? matchedVoucher
      : false;
  };

  const handleApplyTypingVoucher = () => {
    const matchedVoucher = totalVoucher.find(
      (voucher) => voucher.code === typingVoucher,
    );
    if (!matchedVoucher) {
      setError('enterVoucherError');
      return;
    }

    const isValidVoucher = validateVoucherByRegion({
      voucher: matchedVoucher,
      country: isVietNam ? VOUCHER_REGION.VN : VOUCHER_REGION.NON_VN,
    });
    if (!isValidVoucher) {
      setError('notValidVoucherTypeInCountry');
      return;
    }

    if (
      autoRenewParam &&
      matchedVoucher.policy?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.PREPAID
    ) {
      setError('notSatisfiedVoucherError');
      return;
    }

    // Check if this voucher is for non-auto-renew order
    if (
      !autoRenewParam &&
      matchedVoucher.policy?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW
    ) {
      setError('notSatisfiedVoucherError');
      return;
    }

    const satisfiedTime = verifyTime(matchedVoucher);
    const satisfiedPolicy = verifyPolicy(matchedVoucher, currPackage.id);
    if (!satisfiedPolicy) {
      setError('notSatisfiedVoucherError');
      return;
    }
    if (!satisfiedTime) {
      setError('expiredVoucherError');
      return;
    }
    if (!matchedVoucher.remainingAmount) {
      setError('outOfVouchersError');
      return;
    }
    if (matchedVoucher.usedAmount >= matchedVoucher.numReuse) {
      setError('notEnoughAmountError');
      return;
    }
    if (
      matchedVoucher.type === VOUCHER_TYPE.FIXED_CHARACTER &&
      !isFixedCharacterVoucherUsable({
        voucher: matchedVoucher,
        price: orderPrice,
      })
    ) {
      setError('notSatisfiedVoucherError');
      return;
    }
    applyVoucher(matchedVoucher);
    setTypingVoucher('');
    setError(null);
    setOpenVoucherCollapse(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyTypingVoucher();
  };

  const checkCanApplyVoucher = (
    autoVoucherCode,
    listVoucherToSelect = vouchersDisplay,
  ) => {
    const matchedVoucher = listVoucherToSelect.find(
      (voucher) => voucher.code === autoVoucherCode,
    );

    const satisfiedPolicy = matchedVoucher
      ? verifyPolicy(matchedVoucher, currPackage.id)
      : false;

    const satisfiedTime = verifyTime(matchedVoucher);

    // minimumOrderPrice is set with VND currency and should only be compare with VND price of package
    const isFixedCharacterPriceMet =
      matchedVoucher?.type === VOUCHER_TYPE.FIXED_CHARACTER
        ? isFixedCharacterVoucherUsable({
            voucher: matchedVoucher,
            price: orderPrice,
          })
        : true;

    const validVoucher =
      satisfiedPolicy &&
      matchedVoucher &&
      satisfiedTime &&
      matchedVoucher.usedAmount < matchedVoucher.numReuse &&
      matchedVoucher.remainingAmount &&
      isFixedCharacterPriceMet;

    return validVoucher;
  };

  const newAutoSelectVoucher = (vouchersToSelect, allVouchers) => {
    let voucherCode = voucherCodeParamURL;
    let matchedVoucher = validateVoucher(voucherCode);
    if (matchedVoucher) {
      applyVoucher(matchedVoucher);
      return;
    }

    if (vouchersToSelect.length === 0) {
      dispatch(actions.order.updateVoucher(null));

      return;
    }

    voucherCode = vouchersToSelect[0]?.code;
    matchedVoucher = checkSelectedVoucher(
      voucherCode,
      vouchersToSelect,
      allVouchers,
    );
    if (matchedVoucher) {
      applyVoucher(matchedVoucher);
    }
  };

  const selectRandomAutoVoucher = (listAutoVoucher) => {
    const randomPosition = getRandomIntNumber(0, listAutoVoucher.length - 1);
    const autoVoucher = listAutoVoucher[randomPosition];
    const { voucherCode } = autoVoucher;

    const canApplyVoucher = checkCanApplyVoucher(voucherCode, totalVoucher);

    if (canApplyVoucher) {
      const matchedVoucher = totalVoucher.find(
        (voucher) => voucher.code === voucherCode,
      );

      dispatch(
        actions.voucher.applyVoucher({
          ...matchedVoucher,
          displayText: generateDisplayVoucherName(matchedVoucher),
        }),
      );
      dispatch(actions.order.updateVoucher(matchedVoucher));
    }
  };

  const fetchTotalVouchers = async () => {
    const data = await apis.vouchers.getVouchers();
    if (data.status) {
      setTotalVoucher(data.result);
    }
  };

  const handleSelectVoucher = (voucher) => {
    if (voucherState?.id === voucher.id) {
      dispatch(actions.order.updateVoucher(null));
      changeRoute(undefined);
    } else {
      dispatch(actions.order.updateVoucher(voucher));
      changeRoute(voucher.code);
      setOpenVoucherCollapse(false);
    }
  };

  useEffect(() => {
    fetchTotalVouchers();
  }, []);

  useEffect(() => {
    if (currPackage?.code) {
      let useableVoucher = filterUseableVoucher(totalVoucher, currPackage?.id);
      // sort voucher by type
      useableVoucher = getOptimalVouchersForPackage({
        pkg: {
          ...currPackage,
          ...(isCustomPackage && {
            price: custom.price,
            vndPrice: custom.vndPrice,
            custom: { credits: custom.credits },
          }),
        },
        vouchers: useableVoucher,
      });

      // set voucher to display
      setVouchersDisplay(useableVoucher);

      newAutoSelectVoucher(useableVoucher, totalVoucher);
      // TODO: remove auto vouchers
      // These auto vouchers come from src/containers/Banner
      if (autoVouchers.length > 0 && !voucherState?.code)
        selectRandomAutoVoucher(autoVouchers);
    }
  }, [
    currPackage?.code,
    currPackage?.price,
    custom.price,
    custom.vndPrice,
    custom.credits,
    totalVoucher,
  ]);

  const discountPrice = useMemo(() => {
    const calDiscountPrice = isVietNam
      ? calculateCharacterPurchase?.packagePrice -
        calculateCharacterPurchase?.customPrice
      : Math.round(
          (calculateCharacterPurchase?.usdPackagePrice -
            calculateCharacterPurchase?.customUsdPrice) *
            100,
        ) / 100;

    return calDiscountPrice;
  }, [isVietNam, calculateCharacterPurchase]);

  const bulkDiscountView = useMemo(() => {
    if (!discountPrice || !isUIBuyTopupCharacters) return <></>;
    return (
      <Box className="package-info-card">
        <Typography className="package-info-title">
          {t('bulkDiscount')}
        </Typography>
        <Box className="package-info-detail">
          <div className="cal-price-voucher">
            <span className="info-character">
              {isVietNam ? (
                <>
                  -{' '}
                  <span className="discount-unit-price underline font-size-16">
                    đ
                  </span>
                  {discountPrice?.toLocaleString(language)}
                </>
              ) : (
                <>
                  - <span className="discount-unit-price font-size-16">$</span>
                  {discountPrice?.toLocaleString(language, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </>
              )}
            </span>
          </div>
        </Box>
      </Box>
    );
  }, [discountPrice, language, isVietNam]);

  if (isUIBuyTopupCharacters)
    return (
      <StyledVoucherBox>
        <Divider />
        {discountPrice > 0 && (
          <>
            <Typography className="info-title">
              {t('promotionOrder')}
            </Typography>
            {isUIBuyTopupCharacters && bulkDiscountView}
          </>
        )}
      </StyledVoucherBox>
    );

  return (
    <StyledVoucherBox>
      <Divider />
      <Typography className="info-title">{t('promotionOrder')}</Typography>
      <Box
        className="add-voucher-wrapper"
        onClick={() => setOpenVoucherCollapse(!openVoucherCollapse)}
        data-id="promo-toggle"
        data-status-promo-toggle={openVoucherCollapse ? 'expand' : 'collapse'}
      >
        <Typography className="add-voucher-title">
          {t('addCodesAndOffers')}
        </Typography>
        {openVoucherCollapse ? (
          <ExpandLess className="expand-icon" />
        ) : (
          <ExpandMore className="expand-icon" />
        )}
      </Box>
      {openVoucherCollapse && (
        <Box>
          <Box display="flex" gap="8px">
            <TextField
              size="small"
              className="text-field-request"
              variant="outlined"
              value={typingVoucher}
              placeholder={t('enterPromotionCode')}
              onChange={handleChangeTypingVoucher}
              onKeyDown={handleKeyDown}
              data-id="input-value-promo-code"
              data-promo-code={typingVoucher}
            />
            <Button
              color="primary"
              variant="contained"
              className="apply-button"
              disabled={!typingVoucher}
              onClick={handleApplyTypingVoucher}
              data-id="button-apply-promo-code"
              data-promo-code={typingVoucher}
            >
              {t('apply')}
            </Button>
          </Box>
          {error && (
            <Typography
              data-id="form-validation-payment-error"
              data-field="promo-code"
              data-message={t(error)}
              data-form-name="payment-info"
              className="voucher-error"
            >
              {t(error)}
            </Typography>
          )}
        </Box>
      )}
      <Collapse in={openVoucherCollapse} timeout="auto" unmountOnExit>
        {vouchersDisplay
          ?.filter((voucher) => voucher?.satisfiedPolicy)
          .map((voucher) => (
            <VoucherBox
              voucher={voucher}
              autoVouchers={autoVouchers}
              setSelectedVoucher={() => handleSelectVoucher(voucher)}
              selectedVoucher={voucherState?.id === voucher.id}
              checkCanApplyVoucher={checkCanApplyVoucher}
              credits={credits}
            />
          ))}
      </Collapse>

      {voucherState && (
        <SelectedVoucherBox
          voucher={voucherState}
          setSelectedVoucher={() => handleSelectVoucher(voucherState)}
          customPackagePrice={custom?.price}
          credits={credits}
        />
      )}
      <Divider />
    </StyledVoucherBox>
  );
};

export default Voucher;
